import { useEffect, useState } from 'react';

import type { PDFDocumentProxy } from 'pdfjs-dist';

export type PdfJs = typeof import('pdfjs-dist');

const WORKER_SRC = '/pdf.worker.legacy.mjs';

let pdfJsPromise: Promise<PdfJs> | undefined;

export function loadPdfJs(): Promise<PdfJs> {
  if (!pdfJsPromise) {
    pdfJsPromise = import('pdfjs-dist/legacy/build/pdf.mjs').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = WORKER_SRC;
      return pdfjs;
    });
  }
  return pdfJsPromise;
}

export type PdfDocumentState =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'ready';
      pdfjs: PdfJs;
      pdf: PDFDocumentProxy;
      firstPage: { width: number; height: number };
    };

export function usePdfDocument(bytes: Uint8Array): PdfDocumentState {
  const [state, setState] = useState<PdfDocumentState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    let task: { destroy: () => Promise<void>; promise: Promise<PDFDocumentProxy> } | undefined;
    let opened: PDFDocumentProxy | undefined;

    const release = () => {
      void opened?.destroy();
      void task?.destroy();
    };

    (async () => {
      const pdfjs = await loadPdfJs();
      if (cancelled) {
        return;
      }
      task = pdfjs.getDocument({
        data: bytes,
        isEvalSupported: false,
        enableXfa: false,
        disableAutoFetch: true,
        disableStream: true,
      });
      opened = await task.promise;
      if (cancelled) {
        release();
        return;
      }
      const page = await opened.getPage(1);
      if (cancelled) {
        release();
        return;
      }
      const viewport = page.getViewport({ scale: 1 });
      setState({
        status: 'ready',
        pdfjs,
        pdf: opened,
        firstPage: { width: viewport.width, height: viewport.height },
      });
    })().catch(() => {
      release();
      if (!cancelled) {
        setState({ status: 'error' });
      }
    });

    return () => {
      cancelled = true;
      release();
    };
  }, [bytes]);

  return state;
}
